// Read stored classifications back for display — `POST
// /api/ontology/tags/classifications` plus the React hook the Tags page uses.

import { useCallback, useEffect, useRef, useState } from 'react'

import type { TagClassificationRecord } from './types'
import { classificationFromJson, postJson } from './wire'
import { trackDataFlight } from '../../../telemetry/navigation-timing'

export async function fetchTagClassifications(
  nodeIds: string[],
  signal?: AbortSignal
): Promise<Map<string, TagClassificationRecord>> {
  const out = new Map<string, TagClassificationRecord>()
  if (nodeIds.length === 0) return out
  const resp = await postJson<{ classifications?: unknown[] }>(
    '/tags/classifications',
    { nodeIds },
    signal
  )
  for (const raw of resp.classifications ?? []) {
    const rec = classificationFromJson(raw)
    if (rec && !out.has(rec.nodeId)) out.set(rec.nodeId, rec)
  }
  return out
}

export interface TagClassificationsQueryResult {
  data: Map<string, TagClassificationRecord> | undefined
  isLoading: boolean
  error: string | undefined
  refetch: () => void
}

/**
 * Fetch stored classifications for graph node IDs. Keyed by the sorted ID
 * list so reorderings don't refetch; `refetch` is wired by the Tags page
 * to refresh after a classification run completes.
 */
export function useTagClassificationsQuery(nodeIds: string[]): TagClassificationsQueryResult {
  const [data, setData] = useState<Map<string, TagClassificationRecord> | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [refreshTick, setRefreshTick] = useState(0)
  const latestRequestId = useRef(0)

  const key = JSON.stringify([...nodeIds].sort())
  const idsRef = useRef(nodeIds)
  idsRef.current = nodeIds

  useEffect(() => {
    if (idsRef.current.length === 0) {
      setData(new Map())
      setIsLoading(false)
      setError(undefined)
      return
    }
    const ac = new AbortController()
    const requestId = ++latestRequestId.current
    setIsLoading(true)
    setError(undefined)
    // Non-Connect fetch — feed TTFD explicitly so the visible-row
    // classifications keep the navigation window open until they resolve.
    trackDataFlight(fetchTagClassifications(idsRef.current, ac.signal))
      .then((value) => {
        if (requestId !== latestRequestId.current) return
        setData(value)
        setIsLoading(false)
      })
      .catch((err: unknown) => {
        if (requestId !== latestRequestId.current || ac.signal.aborted) return
        setError(err instanceof Error ? err.message : String(err))
        setIsLoading(false)
      })
    return () => {
      ac.abort()
    }
  }, [key, refreshTick])

  const refetch = useCallback(() => setRefreshTick((n) => n + 1), [])

  return { data, isLoading, error, refetch }
}
